// 任务板结构化模型(weekly_task_board_json)与 adapter。
// 设计取向:不破坏既有后端。前端把现有资料(weekly_boards.business_strategy / platform_strategy /
// content_rhythm + tasks + task_outputs + assets)『派生』成任务驱动版型所需的结构;
// 若策略大脑日后在 platform_strategy[平台] 里补了 execution_instructions / strategy_detail 等更精细栏位,
// adapter 会优先采用,否则就地派生。这样新版任务板对【既有所有策略】立即可用,且不需要重新生成。
import type { DbWeeklyBoard, DbTask, DbTaskOutput, DbAsset, ContentPack } from '@/api/astraea';

export type TaskStatus = 'pending' | 'generating' | 'generated' | 'needs_review' | 'completed' | 'failed';
export type AssetStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface TaskBrief {
  title: string;
  goal: string;
  audience: string[];
  lead_path: string;
  next_action: string;
  display_summary: string;
}

export interface TbGeneratedContent {
  content_type: 'note' | 'short_video' | 'talking_avatar' | string;
  title?: string;
  cover_title?: string;
  body?: string;
  hook?: string;
  script?: string;
  shot_list?: string[];
  subtitle_text?: string;
  voice_style?: string;
  video_goal?: string;
  cta?: string;
  hashtags?: string[];
}

export interface TbLeadMagnet { asset_type?: string; asset_title?: string }
export interface TbPrivateDomain {
  comment_keyword?: string;
  public_reply?: string;
  private_reply?: string;
  follow_up_question?: string;
  lead_magnet?: TbLeadMagnet;
  next_step?: string;
}

export type AssetReqType = 'cover_image' | 'voice' | 'talking_avatar_video' | 'dynamic_video';
export interface AssetRequest {
  asset_request_id: string;
  asset_type: AssetReqType;
  label: string;
  provider_type: string;
  status: AssetStatus;
  result?: { url?: string; asset_id?: number; mode?: string } | null;
}

export interface SuccessMetric {
  metric_key: string;
  label: string;
  target: string;
  input_type: 'number' | 'text';
}

export interface TbStrategyDetail {
  reason?: string;
  audience_analysis?: string;
  platform_reason?: string;
  risk_notes?: string;
  fallback_plan?: string;
}

export interface TaskResultAsset { asset_type: string; asset_url: string }
export interface TaskResult {
  content_generated: boolean;
  assets_generated: TaskResultAsset[];
  last_generated_at: string | null;
  download_available: boolean;
}

export interface BoardTask {
  task_id: number;
  task_order: number;
  task_status: TaskStatus;
  platform: string;
  format: string;
  task_type: string;
  day_index: number | null;
  published: boolean;
  downloaded: boolean;
  task_brief: TaskBrief;
  execution_instructions: string[];
  generated_content: TbGeneratedContent | null;
  private_domain: TbPrivateDomain;
  asset_requests: AssetRequest[];
  success_metrics: SuccessMetric[];
  strategy_detail: TbStrategyDetail;
  result: TaskResult;
}

export interface WeeklySummary {
  goal: string;
  focus: string[];
  main_audience: string[];
  main_platforms: string[];
  private_domain_path: string;
  summary_text: string;
}

export interface TodayPriority {
  task_id: number | null;
  title: string;
  platform: string;
  action_label: string;
  reason: string;
}

export interface RhythmDay {
  day: string;
  goal: string;
  task_ids: number[];
}

export interface WeeklyTaskBoard {
  board_id: number;
  role_id: number;
  role_name: string;
  board_status: string;
  weekly_summary: WeeklySummary;
  today_priority: TodayPriority | null;
  weekly_rhythm: RhythmDay[];
  tasks: BoardTask[];
  board_metrics: { tasks_total: number; tasks_completed: number; tasks_pending: number };
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

type PlatformStrategy = Record<string, unknown>;

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
function asStringArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string') as string[] : [];
}

// 从一段话里抽出【关键词】(评论触发词);取不到就回传原本第一个候选
function extractBracketKeyword(...texts: string[]): string {
  for (const t of texts) {
    const m = t && t.match(/[【\[]([^】\]]{1,12})[】\]]/);
    if (m) return m[1];
  }
  return '';
}

function nextActionLabel(taskType: string): string {
  if (taskType === 'short_video') return '生成短视频脚本';
  if (taskType === 'article') return '生成图文';
  return '生成内容';
}
function formatLabel(taskType: string): string {
  if (taskType === 'short_video') return '短视频';
  if (taskType === 'article') return '图文';
  return '内容';
}

function buildExecutionInstructions(ps: PlatformStrategy, taskType: string): string[] {
  const explicit = asStringArray(ps.execution_instructions);
  if (explicit.length) return explicit.slice(0, 5);
  const out: string[] = [];
  const topic = asString(ps.topic) || asString(ps.title);
  if (topic) out.push(`发一${taskType === 'short_video' ? '条短视频' : '篇图文'}：${topic}`);
  const hts = (ps.high_traffic_structure || {}) as Record<string, unknown>;
  const opening = asString(hts.opening_hook);
  if (opening) out.push(`开头就抓住人：${opening}`);
  const body = asStringArray(hts.body_structure);
  for (const b of body.slice(0, 2)) out.push(b);
  const cta = asString(hts.cta);
  if (cta) out.push(`结尾引导：${cta}`);
  return out.slice(0, 5);
}

function buildGeneratedContent(output: DbTaskOutput | undefined, taskType: string): TbGeneratedContent | null {
  if (!output) return null;
  const pack: ContentPack = output.pack || {};
  if (taskType === 'short_video') {
    return {
      content_type: 'short_video',
      title: output.title || '',
      hook: pack.opening_hook || '',
      script: output.body || pack.body || '',
      shot_list: [],
      subtitle_text: '',
      cta: pack.private_domain_cta || pack.comment_trigger || '',
    };
  }
  return {
    content_type: 'note',
    title: output.title || '',
    cover_title: output.title || '',
    body: output.body || pack.body || '',
    cta: pack.private_domain_cta || pack.comment_trigger || '',
    hashtags: [],
  };
}

function buildPrivateDomain(ps: PlatformStrategy, output: DbTaskOutput | undefined, pdPlan: Record<string, unknown>, order: number, followUpFallback: string): TbPrivateDomain {
  const pack: ContentPack = output?.pack || {};
  const keywords = asStringArray(pdPlan.comment_keywords);
  const keyword =
    extractBracketKeyword(pack.comment_trigger || '', pack.private_domain_cta || '', asString((ps.high_traffic_structure as Record<string, unknown>)?.cta)) ||
    keywords[order] || keywords[0] || '';
  const leadTitle = asString(ps.lead_magnet) || pack.lead_magnet_asset?.title || asStringArray(pdPlan.lead_magnets)[0] || '';
  // 私域承接不可断在「私信发资料」:补 follow_up_question(优先策略大脑任务级,缺则从 dm_playbook 取一句,不硬写死)
  const followUp = asString(ps.follow_up_question) || followUpFallback || '';
  return {
    comment_keyword: keyword,
    public_reply: pack.public_reply || asString(pdPlan.public_reply_template),
    private_reply: pack.private_reply || asString(pdPlan.private_reply_template),
    follow_up_question: followUp,
    lead_magnet: { asset_type: pack.lead_magnet_asset?.type || '资料包', asset_title: leadTitle },
    next_step: asString(pdPlan.next_step) || '依对方回答,分流到社群 / 表单 / 产品 / 诊断',
  };
}

function videoModeMatches(asset: DbAsset, kinds: string[]): boolean {
  const mode = (asset.meta && (asset.meta as Record<string, unknown>).mode) as string | undefined;
  return !!mode && kinds.includes(mode);
}

function buildAssetRequests(
  task: DbTask,
  taskVideos: DbAsset[],
  taskAudios: DbAsset[],
  coverImage: { url?: string } | null,
): AssetRequest[] {
  const reqs: AssetRequest[] = [];
  const readyAudio = taskAudios.find((a) => a.url);
  const talkingVideo = taskVideos.find((v) => v.status === 'ready' && v.url && videoModeMatches(v, ['heygen_photo', 'digital_human', 'selfie_talk']));
  const dynamicVideo = taskVideos.find((v) => v.status === 'ready' && v.url && videoModeMatches(v, ['image_video', 'minimax_video']));
  const genTalkingVideo = taskVideos.find((v) => videoModeMatches(v, ['heygen_photo', 'digital_human', 'selfie_talk']) && v.status === 'generating');
  const genDynamicVideo = taskVideos.find((v) => videoModeMatches(v, ['image_video', 'minimax_video']) && v.status === 'generating');

  if (task.task_type === 'short_video') {
    reqs.push({
      asset_request_id: `voice_${task.id}`, asset_type: 'voice', label: '生成配音', provider_type: 'voice_generation',
      status: readyAudio ? 'completed' : 'pending', result: readyAudio ? { url: readyAudio.url || undefined, asset_id: readyAudio.id } : null,
    });
    reqs.push({
      asset_request_id: `talk_${task.id}`, asset_type: 'talking_avatar_video', label: '生成自拍口播影片', provider_type: 'talking_avatar',
      status: talkingVideo ? 'completed' : genTalkingVideo ? 'processing' : 'pending',
      result: talkingVideo ? { url: talkingVideo.url || undefined, asset_id: talkingVideo.id, mode: (talkingVideo.meta as Record<string, unknown> | null)?.mode as string || 'selfie_talk' } : null,
    });
    reqs.push({
      asset_request_id: `dyn_${task.id}`, asset_type: 'dynamic_video', label: '生成形象动态影片', provider_type: 'image_to_video',
      status: dynamicVideo ? 'completed' : genDynamicVideo ? 'processing' : 'pending',
      result: dynamicVideo ? { url: dynamicVideo.url || undefined, asset_id: dynamicVideo.id, mode: 'image_video' } : null,
    });
  } else {
    reqs.push({
      asset_request_id: `cover_${task.id}`, asset_type: 'cover_image', label: '生成封面图', provider_type: 'text_to_image',
      status: coverImage?.url ? 'completed' : 'pending', result: coverImage?.url ? { url: coverImage.url } : null,
    });
    reqs.push({
      asset_request_id: `voice_${task.id}`, asset_type: 'voice', label: '生成配音(选填)', provider_type: 'voice_generation',
      status: readyAudio ? 'completed' : 'pending', result: readyAudio ? { url: readyAudio.url || undefined, asset_id: readyAudio.id } : null,
    });
  }
  return reqs;
}

function buildSuccessMetrics(ps: PlatformStrategy, bsMetrics: Record<string, unknown>): SuccessMetric[] {
  let list = asStringArray(ps.success_metrics);
  if (!list.length) {
    list = [
      ...asStringArray(bsMetrics.content_metrics),
      ...asStringArray(bsMetrics.lead_metrics),
      ...asStringArray(bsMetrics.private_domain_metrics),
    ];
  }
  return list.slice(0, 5).map((label, i) => ({ metric_key: `m${i}`, label, target: '', input_type: 'text' as const }));
}

function buildStrategyDetail(ps: PlatformStrategy, board: DbWeeklyBoard): TbStrategyDetail {
  const explicit = (ps.strategy_detail || null) as TbStrategyDetail | null;
  if (explicit && (explicit.reason || explicit.audience_analysis || explicit.platform_reason)) return explicit;
  const risk = (ps.risk_control || {}) as Record<string, unknown>;
  const riskNotes = [asString(risk.sensitive_risk), ...asStringArray(risk.must_not_include)].filter(Boolean).join('；');
  return {
    reason: asString(ps.platform_goal) || board.why_this_week || '',
    audience_analysis: asString(ps.target_audience),
    platform_reason: asString(ps.content_type) ? `适合的内容形式:${asString(ps.content_type)}` : '',
    risk_notes: riskNotes,
    fallback_plan: '若数据不理想,下一篇可换更直接的痛点切角或更强的清单承接物。',
  };
}

export interface AdapterExtras {
  videos: DbAsset[];
  audios: DbAsset[];
  coverImages?: Record<number, { url?: string }>;
  // 任务级 follow_up_question 的备援:策略大脑没给时,从角色 dm_playbook 取一句(由 board 页传入,不硬写死)
  followUpFallback?: string;
}

export function buildTaskBoard(
  board: DbWeeklyBoard,
  tasks: DbTask[],
  outputs: Record<number, DbTaskOutput>,
  extras: AdapterExtras,
  roleName: string,
): WeeklyTaskBoard {
  const bs = (board.business_strategy || {}) as Record<string, unknown>;
  const pdPlan = (bs.private_domain_plan || {}) as Record<string, unknown>;
  const bsMetrics = (bs.success_metrics || {}) as Record<string, unknown>;
  const platformStrategy = (board.platform_strategy || {}) as Record<string, PlatformStrategy>;
  const rhythm = (board.content_rhythm || {}) as Record<string, string>;

  const boardTasks: BoardTask[] = tasks.map((t, idx) => {
    const ps = platformStrategy[t.platform] || {};
    const output = outputs[t.id];
    const taskVideos = extras.videos.filter((v) => v.task_id === t.id);
    const taskAudios = extras.audios.filter((a) => a.task_id === t.id);
    const cover = extras.coverImages?.[t.id] || null;
    const readyVideo = taskVideos.find((v) => v.status === 'ready' && v.url);
    const hasAsset = !!output || !!readyVideo || taskAudios.some((a) => a.url);
    const status: TaskStatus = t.published ? 'completed' : hasAsset ? 'generated' : 'pending';
    const audience = asString(ps.target_audience)
      ? asString(ps.target_audience).split(/[、,，/]/).map((s) => s.trim()).filter(Boolean).slice(0, 3)
      : asStringArray((bs.audience_targets as { audience_type?: string }[] | undefined)?.map((a) => a.audience_type || '') as unknown).slice(0, 3);
    const leadPath = asString(ps.private_domain_path) || asStringArray(pdPlan.path).join(' → ');
    // 优先用策略大脑直接产出的 task_brief(本版 AI 已结构化输出);缺漏栏位再就地派生
    const aiBrief = (ps.task_brief || null) as Partial<TaskBrief> | null;
    const derivedBrief: TaskBrief = {
      title: t.title || asString(ps.title) || '(待生成标题)',
      goal: asString(ps.content_purpose) ? `${asString(ps.content_purpose)}·${asString(ps.platform_goal).slice(0, 24)}` : asString(ps.platform_goal).slice(0, 30),
      audience,
      lead_path: leadPath.slice(0, 40),
      next_action: nextActionLabel(t.task_type),
      display_summary: (asString(ps.topic) || asString(ps.platform_goal) || t.title || '').slice(0, 80),
    };
    const brief: TaskBrief = aiBrief && (aiBrief.title || aiBrief.display_summary)
      ? {
          title: asString(aiBrief.title) || derivedBrief.title,
          goal: asString(aiBrief.goal) || derivedBrief.goal,
          audience: Array.isArray(aiBrief.audience) && aiBrief.audience.length ? aiBrief.audience.slice(0, 3) : derivedBrief.audience,
          lead_path: asString(aiBrief.lead_path) || derivedBrief.lead_path,
          next_action: asString(aiBrief.next_action) || derivedBrief.next_action,
          display_summary: asString(aiBrief.display_summary) || derivedBrief.display_summary,
        }
      : derivedBrief;
    const result: TaskResult = {
      content_generated: !!output,
      assets_generated: [
        ...taskVideos.filter((v) => v.status === 'ready' && v.url).map((v) => ({ asset_type: 'video', asset_url: v.url as string })),
        ...taskAudios.filter((a) => a.url).map((a) => ({ asset_type: 'audio', asset_url: a.url as string })),
        ...(cover?.url ? [{ asset_type: 'cover_image', asset_url: cover.url }] : []),
      ],
      last_generated_at: output?.created_at || readyVideo?.created_at || null,
      download_available: hasAsset,
    };
    return {
      task_id: t.id,
      task_order: idx + 1,
      task_status: status,
      platform: t.platform,
      format: formatLabel(t.task_type),
      task_type: t.task_type,
      day_index: t.day_index ?? null,
      published: !!t.published,
      downloaded: !!t.downloaded,
      task_brief: brief,
      execution_instructions: buildExecutionInstructions(ps, t.task_type),
      generated_content: buildGeneratedContent(output, t.task_type),
      private_domain: buildPrivateDomain(ps, output, pdPlan, idx, extras.followUpFallback || ''),
      asset_requests: buildAssetRequests(t, taskVideos, taskAudios, cover),
      success_metrics: buildSuccessMetrics(ps, bsMetrics),
      strategy_detail: buildStrategyDetail(ps, board),
      result,
    };
  });

  // 今日优先:取最早(day_index 最小)且尚未完成的任务;都完成就取第一个
  const sortedByDay = [...boardTasks].sort((a, b) => (a.day_index ?? 99) - (b.day_index ?? 99));
  const priorityTask = sortedByDay.find((t) => t.task_status !== 'completed') || sortedByDay[0] || null;
  const today_priority: TodayPriority | null = priorityTask
    ? {
        task_id: priorityTask.task_id,
        title: priorityTask.task_brief.title,
        platform: priorityTask.platform,
        action_label: priorityTask.generated_content ? '先把这篇承接做完' : `先${priorityTask.task_brief.next_action}`,
        reason: priorityTask.strategy_detail.reason || '这篇最适合先建立流量入口与私信承接。',
      }
    : null;

  const weekly_rhythm: RhythmDay[] = DAY_KEYS.map((k, i) => ({
    day: `第 ${i + 1} 天`,
    goal: rhythm[k] || '',
    task_ids: boardTasks.filter((t) => t.day_index === i + 1).map((t) => t.task_id),
  })).filter((d) => d.goal || d.task_ids.length);

  const mainPlatforms = [...new Set(boardTasks.map((t) => t.platform))];
  const focus = asStringArray(bs.expected_effect).slice(0, 3).length
    ? asStringArray(bs.expected_effect).slice(0, 3)
    : asStringArray(bsMetrics.content_metrics).slice(0, 3);
  const weekly_summary: WeeklySummary = {
    goal: asString(bs.what_you_get) || board.weekly_goal || '',
    focus,
    main_audience: ((bs.audience_targets as { audience_type?: string }[] | undefined) || []).map((a) => a.audience_type || '').filter(Boolean).slice(0, 3),
    main_platforms: mainPlatforms,
    private_domain_path: asStringArray(pdPlan.path).join(' → '),
    summary_text: board.why_this_week || asString(bs.what_you_get) || board.weekly_goal || '',
  };

  const completed = boardTasks.filter((t) => t.task_status === 'completed').length;
  return {
    board_id: board.id,
    role_id: board.character_id,
    role_name: roleName,
    board_status: 'ready',
    weekly_summary,
    today_priority,
    weekly_rhythm,
    tasks: boardTasks,
    board_metrics: { tasks_total: boardTasks.length, tasks_completed: completed, tasks_pending: boardTasks.length - completed },
  };
}
